import { Component } from '@theme/component';

/**
 * @typedef {object} ProductDetailsSpecAccordionRefs
 * @property {HTMLButtonElement} toggleButton - The clickable header that opens/closes the accordion.
 */

/**
 * A small, self-contained accordion used by product-details.liquid. It is built from plain
 * <div>/<button> markup instead of the theme's shared <details>/<summary> + accordion-custom
 * component, whose own open/close animation (block-size/opacity) fought with this block's
 * max-height + fade based collapsed preview.
 * @extends Component<ProductDetailsSpecAccordionRefs>
 */
class ProductDetailsSpecAccordion extends Component {
  toggle() {
    const wasOpen = this.hasAttribute('open');
    this.toggleAttribute('open', !wasOpen);
    this.refs.toggleButton?.setAttribute('aria-expanded', String(!wasOpen));

    if (wasOpen) this.#keepInView();
  }

  /**
   * Closing can shrink the page enough that the browser clamps the scroll position to the new
   * (shorter) max, which visually jumps the viewport toward the footer. Once the collapse
   * settles, pull this accordion back into view if that happened.
   */
  #keepInView() {
    const content = this.querySelector('.product-details-spec__content');
    let done = false;

    const restore = () => {
      if (done) return;
      done = true;

      const rect = this.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        this.scrollIntoView({ block: 'nearest' });
      }
    };

    // The transition (if any) fires this; a timeout covers reduced-motion / 0-duration cases,
    // where `transitionend` never fires at all.
    content?.addEventListener('transitionend', restore, { once: true });
    setTimeout(restore, 400);
  }
}

if (!customElements.get('product-details-spec-accordion-component')) {
  customElements.define('product-details-spec-accordion-component', ProductDetailsSpecAccordion);
}
